import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Skeleton } from '@/components/ui/skeleton'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Label } from '@/components/ui/label'
import { Search, Building2 } from 'lucide-react'
import { useClinicGroups } from '@/hooks/useClinicGroups'
import { clinicGroupService } from '@/services/clinic_group_service'
import { useAuth } from '@/hooks/useAuth'
import { toast } from 'sonner'
import { useDebounce } from '@/hooks/useDebounce'

interface JoinClinicGroupDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  clinicId: number
  providerId: number | null
  onSuccess: () => void
}

export function JoinClinicGroupDialog({
  open,
  onOpenChange,
  clinicId,
  providerId,
  onSuccess,
}: JoinClinicGroupDialogProps) {
  const { token } = useAuth()
  const [search, setSearch] = useState('')
  const [selectedGroupId, setSelectedGroupId] = useState<string>('')
  const [isJoining, setIsJoining] = useState(false)

  const debouncedSearch = useDebounce(search, 300)

  const { clinicGroups, isLoading } = useClinicGroups({
    page: 1,
    pageSize: 999,
    is_active: true,
    provider_id: providerId ?? undefined,
  })

  const filteredGroups = clinicGroups?.filter((group) => {
    if (!debouncedSearch) return true
    const keyword = debouncedSearch.toLowerCase()
    return (
      group.group_name.toLowerCase().includes(keyword) ||
      (group.group_code && group.group_code.toLowerCase().includes(keyword))
    )
  })

  useEffect(() => {
    if (!open) {
      setSearch('')
      setSelectedGroupId('')
    }
  }, [open])

  const handleJoin = async () => {
    if (!token || !selectedGroupId) return

    setIsJoining(true)
    const result = await clinicGroupService.assignClinic(
      parseInt(selectedGroupId, 10),
      clinicId,
      token,
    )
    setIsJoining(false)

    if (result.success) {
      toast.success('已成功加入集團')
      onSuccess()
      onOpenChange(false)
    } else {
      const errorData = result as { error?: { message?: string } }
      toast.error(errorData.error?.message || '加入集團失敗')
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>加入集團</DialogTitle>
          <DialogDescription>選擇要加入的診所集團</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="搜尋集團名稱或代碼..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>

          <div className="border rounded-md overflow-hidden">
            <ScrollArea className="h-[300px] w-full overflow-hidden">
              <div className="w-0 min-w-full overflow-hidden">
                {isLoading ? (
                  <div className="p-4 space-y-3">
                    {[...Array(5)].map((_, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <Skeleton className="h-4 w-4" />
                        <Skeleton className="h-4 flex-1" />
                      </div>
                    ))}
                  </div>
                ) : filteredGroups && filteredGroups.length > 0 ? (
                  <RadioGroup value={selectedGroupId} onValueChange={setSelectedGroupId}>
                    <div className="divide-y">
                      {filteredGroups.map((group) => (
                        <div
                          key={group.clinic_group_id}
                          className="flex items-center gap-3 p-3 hover:bg-muted/50 cursor-pointer overflow-hidden"
                          onClick={() => setSelectedGroupId(String(group.clinic_group_id))}
                        >
                          <RadioGroupItem
                            value={String(group.clinic_group_id)}
                            id={`group-${group.clinic_group_id}`}
                          />
                          <Label
                            htmlFor={`group-${group.clinic_group_id}`}
                            className="flex items-center gap-2 flex-1 min-w-0 cursor-pointer"
                          >
                            <Building2 className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <div className="font-medium truncate">{group.group_name}</div>
                              {group.group_code && (
                                <div className="text-sm text-muted-foreground truncate">
                                  {group.group_code}
                                </div>
                              )}
                            </div>
                          </Label>
                        </div>
                      ))}
                    </div>
                  </RadioGroup>
                ) : (
                  <div className="flex flex-col items-center justify-center h-full py-8 text-muted-foreground">
                    <Building2 className="h-8 w-8 mb-2" />
                    <p>沒有可加入的集團</p>
                  </div>
                )}
              </div>
            </ScrollArea>
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            取消
          </Button>
          <Button
            type="button"
            onClick={handleJoin}
            disabled={!selectedGroupId || isJoining}
          >
            {isJoining ? '加入中...' : '確定加入'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
